import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import type { DocumentTypeSectionsFetchedAction } from '@Application/Organizations/Workflows/Actions/documentTypeSectionsFetched';
import type { OrganizationWorkflowCreatedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowCreated';
import type { OrganizationWorkflowDeactivatedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowDeactivated';
import type { OrganizationWorkflowOptionsSavedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowOptionsSaved';
import type { OrganizationWorkflowRestoredAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowRestored';
import type { OrganizationWorkflowSavedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowSaved';
import type { OrganizationWorkflowsFetchedAction } from '@Application/Organizations/Workflows/Actions/organizationWorkflowsFetched';
import type { WorkflowCatalogFetchedAction } from '@Application/Organizations/Workflows/Actions/workflowCatalogFetched';
import DocumentTypePresetView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypePresetView';
import DocumentTypeSectionsView from '@UI/Store/OrganizationWorkflows/Views/DocumentTypeSectionsView';
import OrganizationWorkflowView from '@UI/Store/OrganizationWorkflows/Views/OrganizationWorkflowView';
import OrganizationWorkflowsView from '@UI/Store/OrganizationWorkflows/Views/OrganizationWorkflowsView';
import WorkflowCatalogView from '@UI/Store/OrganizationWorkflows/Views/WorkflowCatalogView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type OrganizationWorkflowsStore = {
	workflows: OrganizationWorkflowsView | null;
	catalog: WorkflowCatalogView | null;
	sections: DocumentTypeSectionsView | null;
};

const initialState: OrganizationWorkflowsStore = {
	workflows: null,
	catalog: null,
	sections: null,
};

type Actions =
	| OrganizationWorkflowsFetchedAction
	| OrganizationWorkflowCreatedAction
	| OrganizationWorkflowSavedAction
	| OrganizationWorkflowOptionsSavedAction
	| OrganizationWorkflowDeactivatedAction
	| OrganizationWorkflowRestoredAction
	| WorkflowCatalogFetchedAction
	| DocumentTypeSectionsFetchedAction
	| UserRevokedAction;

const whenOrganizationWorkflowsFetched = (
	store: OrganizationWorkflowsStore,
	action: OrganizationWorkflowsFetchedAction,
): OrganizationWorkflowsStore => ({
	...store,
	workflows: new OrganizationWorkflowsView(
		action.payload.map((workflow) => OrganizationWorkflowView.fromPayload(workflow)),
		action.organizationId,
	),
});

// A created, saved or re-optioned workflow replaces the current version of its group, or joins the list.
const whenOrganizationWorkflowPublished = (
	store: OrganizationWorkflowsStore,
	action:
		| OrganizationWorkflowCreatedAction
		| OrganizationWorkflowSavedAction
		| OrganizationWorkflowOptionsSavedAction,
): OrganizationWorkflowsStore => {
	if (!store.workflows || store.workflows.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		workflows: store.workflows.upsertWorkflow(OrganizationWorkflowView.fromPayload(action.payload)),
	};
};

const whenOrganizationWorkflowDeactivated = (
	store: OrganizationWorkflowsStore,
	action: OrganizationWorkflowDeactivatedAction,
): OrganizationWorkflowsStore => {
	if (!store.workflows || store.workflows.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		workflows: store.workflows.setWorkflowActive(action.payload.workflowKey, false),
	};
};

const whenOrganizationWorkflowRestored = (
	store: OrganizationWorkflowsStore,
	action: OrganizationWorkflowRestoredAction,
): OrganizationWorkflowsStore => {
	if (!store.workflows || store.workflows.organizationId !== action.organizationId) {
		return store;
	}

	return {
		...store,
		workflows: store.workflows.setWorkflowActive(action.payload.workflowKey, true),
	};
};

const whenWorkflowCatalogFetched = (
	store: OrganizationWorkflowsStore,
	action: WorkflowCatalogFetchedAction,
): OrganizationWorkflowsStore => ({
	...store,
	catalog: new WorkflowCatalogView(action.payload.map((preset) => new DocumentTypePresetView(preset))),
});

const whenDocumentTypeSectionsFetched = (
	store: OrganizationWorkflowsStore,
	action: DocumentTypeSectionsFetchedAction,
): OrganizationWorkflowsStore => ({
	...store,
	sections: new DocumentTypeSectionsView(action.payload, action.organizationId, action.documentTypeId),
});

const whenUserRevoked = (): OrganizationWorkflowsStore => initialState;

export default registerStore<'organizationWorkflowsStore', Actions>(
	'organizationWorkflowsStore',
	createSliceReducer({
		ORGANIZATION_WORKFLOWS_FETCHED: whenOrganizationWorkflowsFetched,
		ORGANIZATION_WORKFLOW_CREATED: whenOrganizationWorkflowPublished,
		ORGANIZATION_WORKFLOW_SAVED: whenOrganizationWorkflowPublished,
		ORGANIZATION_WORKFLOW_OPTIONS_SAVED: whenOrganizationWorkflowPublished,
		ORGANIZATION_WORKFLOW_DEACTIVATED: whenOrganizationWorkflowDeactivated,
		ORGANIZATION_WORKFLOW_RESTORED: whenOrganizationWorkflowRestored,
		WORKFLOW_CATALOG_FETCHED: whenWorkflowCatalogFetched,
		DOCUMENT_TYPE_SECTIONS_FETCHED: whenDocumentTypeSectionsFetched,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
